/**
 * Strip credentials out of a pathname before it reaches a log line.
 *
 * Every capability-URL surface puts its secret in the PATH, so any logger that
 * records a request path is one careless call away from persisting a live
 * credential. Four shapes are redacted:
 *
 *   - UUIDs — the original case (quote and preference tokens).
 *   - 43-character base64url segments — `randomBytes(32).toString('base64url')`,
 *     which is what the affiliate demo session token is. Added when the poll
 *     route `/api/demo/s/<token>` gained a rate limiter: the limiter's 429 path
 *     logs the pathname through this function, so without the second pattern
 *     the fix would have written live demo tokens into the logs it added.
 *   - 64-character lowercase-hex segments — `crm_quotes.public_token`
 *     (`encode(gen_random_bytes(32), 'hex')`), which rides `/q/<token>` and
 *     `/api/quotes/public/<token>/…`.
 *   - 32-character lowercase-hex segments — `aeo_report_shares.share_token`
 *     (`randomBytes(16).toString('hex')`, `/share/aeo/<token>`) and the
 *     personalized-video `pv_generated_videos.share_token`
 *     (`encode(gen_random_bytes(16), 'hex')`, `/v/<token>`).
 *
 * Every pattern is anchored to a whole path SEGMENT and pinned to an exact
 * length, so none can swallow an ordinary slug: real slugs are shorter, and a
 * collision would have to be drawn from exactly the token's alphabet at exactly
 * its length. The hex classes stay lowercase-only on purpose — all three hex
 * token sources emit lowercase — because a case-insensitive class would start
 * matching ordinary words.
 */
export function redactPathTokens(pathname: string): string {
  return pathname
    .replace(
      /(^|\/)[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi,
      '$1[redacted-uuid]'
    )
    .replace(/(^|\/)[A-Za-z0-9_-]{43}(?=\/|$)/g, '$1[redacted-token]')
    // 64 before 32 so a 256-bit hex segment is not left half-matched.
    .replace(/(^|\/)[0-9a-f]{64}(?=\/|$)/g, '$1[redacted-token]')
    .replace(/(^|\/)[0-9a-f]{32}(?=\/|$)/g, '$1[redacted-token]');
}
